import { httpClient, ApiResponse } from '@/utils';
import {
  IAddOrderCommentResponse,
  IAddOrderCommentDTO,
  IGetOrdersListDTO,
  IOrderDetailsResponse,
  IOrdersListResponse,
  IUpdateOrderDeliveryDTO,
  IUpdateOrderPaymentDTO,
  IUpdateOrderPaymentChannelDTO,
  IUpdateOrderProductsDTO,
  OrdersEndpoints,
  IChangeOrderStatusDTO,
  IGetOrderProductsDTO,
  IGetOrderProductsResponse,
  IGetNovaPoshtaDepartmentsResponse,
  IGetNovaPoshtaDepartmentsDTO,
  IUpdateOrderClientDTO,
  IChangeOrderRegionDTO,
  IChangeOrderManagerDTO,
  IGetNewOrdersCountResponse,
  IGetOrderDetailsParams,
  IRecordAdditionalPaymentDTO,
  IRecordRefundDTO,
  IIssueOrderReturnDTO,
} from '@/models/orders.model';

class OrdersAPI {
  static getOrdersList(data: IGetOrdersListDTO): ApiResponse<IOrdersListResponse> {
    return httpClient.get<IOrdersListResponse>(OrdersEndpoints.ORDERS, { params: data });
  }

  static getOrderProductsList({
    region_id,
    dto,
  }: {
    region_id: string;
    dto?: IGetOrderProductsDTO;
  }): ApiResponse<IGetOrderProductsResponse> {
    return httpClient.get<IGetOrderProductsResponse>(`${OrdersEndpoints.ORDER_PRODUCTS}/${region_id}`, {
      params: dto,
    });
  }

  static getOrderDetails({ id }: IGetOrderDetailsParams): ApiResponse<IOrderDetailsResponse> {
    return httpClient.get<IOrderDetailsResponse>(`${OrdersEndpoints.ORDERS}/${id}`);
  }

  static updateOrderDelivery(data: IUpdateOrderDeliveryDTO): ApiResponse<IUpdateOrderDeliveryDTO> {
    // eslint-disable-next-line @typescript-eslint/naming-convention
    const { order_id, region_id, ...rest } = data;
    return httpClient.patch(`${OrdersEndpoints.ORDERS}/${order_id}/change-delivery/${region_id}`, rest);
  }

  static updateOrderClient({
    order_id,
    data,
  }: {
    order_id: string;
    data: IUpdateOrderClientDTO;
  }): ApiResponse<IUpdateOrderClientDTO> {
    return httpClient.patch(`${OrdersEndpoints.ORDERS}/${order_id}/change-contact-person`, data);
  }

  static updateOrderPayment({ order_id, payment_status }: IUpdateOrderPaymentDTO): ApiResponse<IUpdateOrderPaymentDTO> {
    return httpClient.patch(`${OrdersEndpoints.ORDERS}/${order_id}/change-payment-status`, { payment_status });
  }

  static updateOrderPaymentChannel({
    order_id,
    payment_channel,
  }: IUpdateOrderPaymentChannelDTO): ApiResponse<IUpdateOrderPaymentChannelDTO> {
    return httpClient.patch(`${OrdersEndpoints.ORDERS}/${order_id}/change-payment-channel`, { payment_channel });
  }

  static updateOrderProducts({
    order_id,
    products,
    add_new,
    reason,
  }: IUpdateOrderProductsDTO): ApiResponse<IUpdateOrderProductsDTO> {
    return httpClient.patch(`${OrdersEndpoints.ORDERS}/${order_id}/products`, { products, add_new, reason });
  }

  static recordAdditionalPayment({
    order_id,
    amount,
    payment_channel,
  }: IRecordAdditionalPaymentDTO): ApiResponse<void> {
    return httpClient.post(`${OrdersEndpoints.ORDERS}/${order_id}/record-additional-payment`, {
      amount,
      payment_channel,
    });
  }

  static recordRefund({ order_id, amount }: IRecordRefundDTO): ApiResponse<void> {
    return httpClient.post(`${OrdersEndpoints.ORDERS}/${order_id}/record-refund`, {
      amount,
    });
  }

  static issueOrderReturn({ order_id, all, items }: IIssueOrderReturnDTO): ApiResponse<void> {
    return httpClient.post(`${OrdersEndpoints.ORDERS}/${order_id}/fiscal-returns`, all ? { all } : { items });
  }

  static addOrderComment({
    order_id,
    subOrder_id,
    comment,
  }: IAddOrderCommentDTO): ApiResponse<IAddOrderCommentResponse> {
    return httpClient.post(`${OrdersEndpoints.ORDERS}/${order_id}/sub-order/${subOrder_id}/comments`, { comment });
  }

  static changeOrderStatus({ order_id, status, cancellation_reason, cancellation_reason_text }: IChangeOrderStatusDTO) {
    return httpClient.patch(`${OrdersEndpoints.ORDERS}/${order_id}/change-status`, {
      status,
      ...(cancellation_reason ? { cancellation_reason } : {}),
      ...(cancellation_reason_text ? { cancellation_reason_text } : {}),
    });
  }

  static changeOrderRegion({ order_id, region_id }: IChangeOrderRegionDTO) {
    return httpClient.patch(`${OrdersEndpoints.ORDERS}/${order_id}/change-region`, { region_id });
  }

  static changeOrderManager({ order_id, manager_id }: IChangeOrderManagerDTO): ApiResponse<IOrderDetailsResponse> {
    return httpClient.patch<IOrderDetailsResponse>(`${OrdersEndpoints.ORDERS}/${order_id}/change-manager`, {
      manager_id,
    });
  }

  static getNovaPoshtaDepartments({
    order,
    settlementFrom_id,
    settlementTo_id,
    name,
  }: IGetNovaPoshtaDepartmentsDTO): ApiResponse<IGetNovaPoshtaDepartmentsResponse> {
    return httpClient.get(`${OrdersEndpoints.ORDERS}/${order}/nova-poshta/${settlementFrom_id}/to/${settlementTo_id}`, {
      params: { name },
    });
  }

  static getNewOrdersCount(): ApiResponse<IGetNewOrdersCountResponse> {
    return httpClient.get<IGetNewOrdersCountResponse>(`${OrdersEndpoints.ORDERS}/new-count`);
  }

  static deleteOrder(id: string): ApiResponse<void> {
    return httpClient.delete(`${OrdersEndpoints.ORDERS}/${id}`);
  }
}

export default OrdersAPI;
